/**
 * A shareable address for the Unit Converter state (B-0295): /tool/unit-converter?c=temperature&from=celsius&to=fahrenheit&v=-40.
 * Reading it back is strict: a category, unit or ingredient that does not exist is ignored (the page opens on its
 * defaults) and a value that is not a number is dropped, so a mistyped link never breaks the page.
 */
import { CATEGORIES, COOKING_INGREDIENTS, cookingCategory } from "./units";

export interface UnitLinkState { category: string; from: string; to: string; value: string; ingredient: string }
export type UnitLinkParse = Partial<UnitLinkState>;

const DEFAULT_INGREDIENT = COOKING_INGREDIENTS[0].id;

function unitNames(category: string, ingredient: string): string[] | null {
  if (category === "cooking") return cookingCategory(ingredient).units.map((u) => u.name);
  return CATEGORIES.find((c) => c.id === category)?.units.map((u) => u.name) ?? null;
}

export function parseUnitSearch(search: string): UnitLinkParse {
  const q = new URLSearchParams(search);
  const out: UnitLinkParse = {};
  const c = q.get("c");
  if (!c || !CATEGORIES.some((x) => x.id === c)) return out; // without a valid category the units mean nothing
  out.category = c;
  const ing = q.get("ing");
  if (c === "cooking" && ing && COOKING_INGREDIENTS.some((i) => i.id === ing)) out.ingredient = ing;
  const names = unitNames(c, out.ingredient ?? DEFAULT_INGREDIENT) ?? [];
  const from = q.get("from"), to = q.get("to");
  if (from && names.includes(from)) out.from = from;
  if (to && names.includes(to)) out.to = to;
  const v = q.get("v");
  if (v !== null && v.trim() !== "" && Number.isFinite(Number(v))) out.value = v.trim();
  return out;
}

/** "?c=...&from=...&to=...&v=..." for the state on screen, or "" when it is just the opening state. */
export function buildUnitSearch(s: UnitLinkState & { fromUnit?: string; toUnit?: string }): string {
  const q = new URLSearchParams();
  q.set("c", s.category);
  if (s.category === "cooking" && s.ingredient !== DEFAULT_INGREDIENT) q.set("ing", s.ingredient);
  const from = s.fromUnit ?? s.from, to = s.toUnit ?? s.to;
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  if (s.value.trim() !== "" && Number.isFinite(Number(s.value))) q.set("v", s.value.trim());
  return `?${q.toString()}`;
}
